import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LandingPage() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const heroRef = useRef<HTMLDivElement>(null);

  // Redirect authenticated users to home
  useEffect(() => {
    if (user) {
      navigate('/home');
    }
  }, [user, navigate]);

  // Mouse tracking for parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current && !isTransitioning) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setMousePos({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isTransitioning]);

  // ✨ DOMAIN EXPANSION ANIMATION
  const handleEnter = () => {
    setIsTransitioning(true);

    // Navigate after animation completes
    setTimeout(() => {
      navigate('/login');
    }, 1800);
  };

  return (
    <div className="min-h-screen text-white overflow-hidden relative" style={{ background: '#7ecece' }}>

      {/* Domain Expansion Transition Overlay */}
      {isTransitioning && (
        <>
          {/* Radial expanding circle */}
          <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none">
            <div className="absolute w-0 h-0 bg-white-600/30 rounded-full animate-domain-expand" />
            <div className="absolute w-0 h-0 bg-grey-600/20 rounded-full animate-domain-expand-delayed" />
          </div>

          {/* Vertical slice effects */}
          <div className="fixed inset-0 z-[101] flex pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="flex-1 bg-white-600/80 animate-slice-in"
                style={{
                  animationDelay: `${i * 0.03}s`,
                  transform: 'scaleY(0)',
                }}
              />
            ))}
          </div>

          {/* Power-up flash */}
          <div className="fixed inset-0 z-[99] bg-white animate-power-flash" />

          {/* Domain text reveal */}
          <div className="fixed inset-0 z-[102] flex items-center justify-center pointer-events-none">
            <div className="text-center animate-domain-text">
              <div className="text-6xl font-black text-white mb-4 opacity-0 animate-text-fade-in">
                entering in...
              </div>
              <div className="text-3xl font-light text-black-300 opacity-0 animate-text-fade-in-delayed">
                Behold
              </div>
            </div>
          </div>

          {/* Particle effects */}
          <div className="fixed inset-0 z-[98] pointer-events-none">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white-400 rounded-full animate-particle-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${1 + Math.random()}s`,
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* Halftone dot overlay — subtle texture on the teal */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(13,26,26,0.07) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />

      {/* Navigation */}
      <nav
        className={`relative z-10 flex justify-between items-center px-8 py-6 transition-opacity duration-500 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="flex items-center space-x-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{
              background: '#0d1a1a',
              border: '2px solid #0d1a1a',
              boxShadow: '3px 3px 0 rgba(13,26,26,0.4)',
            }}
          >
            <span
              className="font-black text-xl"
              style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#7ecece', letterSpacing: '1px' }}
            >
              O
            </span>
          </div>
          <div>
            <h1
              className="text-xl tracking-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '5px', color: '#0d1a1a' }}
            >
              RONYX
            </h1>
            <p className="text-[10px] tracking-widest uppercase -mt-0.5" style={{ color: 'rgba(13,26,26,0.5)' }}>
              Network
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate('/login')}
          className="transition-all duration-200"
          style={{
            padding: '10px 25px',
            background: '#0d1a1a',
            border: '2px solid #0d1a1a',
            borderRadius: '6px',
            fontFamily: "'M PLUS Rounded 1c', sans-serif",
            fontSize: '12px',
            fontWeight: 900,
            letterSpacing: '2px',
            color: '#7ecece',
            boxShadow: '3px 3px 0 rgba(13,26,26,0.3)',
            cursor: 'pointer',
          }}
          onMouseEnter={e => {
            (e.target as HTMLButtonElement).style.transform = 'translate(1px,1px)';
            (e.target as HTMLButtonElement).style.boxShadow = '1px 1px 0 rgba(13,26,26,0.3)';
          }}
          onMouseLeave={e => {
            (e.target as HTMLButtonElement).style.transform = '';
            (e.target as HTMLButtonElement).style.boxShadow = '3px 3px 0 rgba(13,26,26,0.3)';
          }}
        >
          ENTER
        </button>
      </nav>

      {/* Hero Section */}
      <div
        ref={heroRef}
        className={`relative z-10 flex flex-col items-center justify-center min-h-[88vh] px-6 transition-all duration-500 ${
          isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}
      >
        <div className="max-w-4xl mx-auto text-center space-y-8">

          {/* Season badge */}
          <div
            className="inline-flex items-center gap-3"
            style={{
              background: '#0d1a1a',
              color: '#7ecece',
              fontSize: '10px',
              fontWeight: 900,
              letterSpacing: '3px',
              padding: '5px 16px',
              borderRadius: '3px',
              border: '2px solid #0d1a1a',
              boxShadow: '2px 2px 0 rgba(13,26,26,0.4)',
              fontFamily: "'M PLUS Rounded 1c', sans-serif",
            }}
          >
            <span
              style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: '#5ab8b8',
                display: 'inline-block',
                animation: 'onyxPulse 1.8s ease-in-out infinite',
              }}
            />
            SEASON ONE · NOW OPEN
          </div>

          {/* Main Headline — extruded block style */}
          <div
            style={{
              transform: `perspective(1000px) rotateX(${mousePos.y * -2}deg) rotateY(${mousePos.x * 2}deg)`,
              lineHeight: 0.85,
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 'clamp(88px, 20vw, 148px)',
                letterSpacing: '6px',
                color: '#f0fafa',
                WebkitTextStroke: '3px #0d1a1a',
                paintOrder: 'stroke fill',
                textShadow: '2px 2px 0 #c8e8e8, 4px 4px 0 #a0d0d0, 6px 6px 0 #78b8b8, 8px 8px 0 #509090, 9px 9px 0 #0d1a1a',
              }}
            >
              RONYX
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 'clamp(42px, 10vw, 76px)',
                letterSpacing: '6px',
                color: '#e8a820',
                WebkitTextStroke: '2.5px #0d1a1a',
                paintOrder: 'stroke fill',
                textShadow: '2px 2px 0 #c8880a, 4px 4px 0 #905800, 5px 5px 0 #0d1a1a',
              }}
            >
              NETWORK
            </span>
          </div>

          <h2
            className="tracking-wide"
            style={{
              fontFamily: "'M PLUS Rounded 1c', sans-serif",
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '2px',
              color: 'rgba(13,26,26,0.5)',
            }}
          >
            A NETWORK FOR ELITE BALL KNOWERS
          </h2>

          <p style={{ color: 'rgba(13,26,26,0.45)', fontSize: '13px', letterSpacing: '1px', fontWeight: 700 }}>
            If you know, you know. If you don't, you don't.
          </p>

          {/* CTA */}
          <div className="flex flex-col items-center gap-4 pt-4">
            <button
              onClick={handleEnter}
              disabled={isTransitioning}
              className="transition-all duration-100 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: '#e8a820',
                border: '3px solid #0d1a1a',
                borderRadius: '7px',
                padding: '15px 52px',
                fontFamily: "'M PLUS Rounded 1c', sans-serif",
                fontSize: '20px',
                fontWeight: 900,
                color: '#0d1a1a',
                letterSpacing: '2px',
                boxShadow: '4px 4px 0 #0d1a1a, 6px 6px 0 #8a5a00',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={e => {
                const btn = e.currentTarget;
                btn.style.transform = 'translate(2px,2px)';
                btn.style.boxShadow = '2px 2px 0 #0d1a1a, 3px 3px 0 #8a5a00';
              }}
              onMouseLeave={e => {
                const btn = e.currentTarget;
                btn.style.transform = '';
                btn.style.boxShadow = '4px 4px 0 #0d1a1a, 6px 6px 0 #8a5a00';
              }}
            >
              STEP INTO THE RING
            </button>

            <p
              style={{
                fontFamily: "'M PLUS Rounded 1c', sans-serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '2px',
                color: 'rgba(13,26,26,0.45)',
              }}
            >
              No malarkey · No mid takes · Just peak
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div
        className={`relative z-10 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,1fr)',
          gap: '3px',
          background: '#0d1a1a',
          borderTop: '3px solid #0d1a1a',
          borderBottom: '3px solid #0d1a1a',
        }}
      >
        {[
          {
            num: '01 · POV',
            title: 'SHARE YOUR POV',
            desc: 'Post your takes, clips, and moments. Image and video support included. Your arc starts here.',
          },
          {
            num: '02 · CIRCLE',
            title: 'BUILD YOUR CIRCLE',
            desc: 'Follow the real ones. Connect with those who actually understand the craft.',
          },
          {
            num: '03 · ENERGY',
            title: 'NO CASUAL ENERGY',
            desc: 'For those who see the vision. The rest can stay on the bench.',
          },
        ].map((f, i) => (
          <div
            key={i}
            className="group cursor-pointer relative"
            style={{
              background: '#f0fafa',
              padding: '32px 26px',
              transition: 'background 0.2s, transform 0.15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#ffffff'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = '#f0fafa'; }}
          >
            {/* corner brackets */}
            <span
              style={{
                position: 'absolute', top: 10, left: 10,
                width: 14, height: 14,
                borderTop: '2.5px solid #0d1a1a',
                borderLeft: '2.5px solid #0d1a1a',
                display: 'block',
              }}
            />
            <span
              style={{
                position: 'absolute', bottom: 10, right: 10,
                width: 14, height: 14,
                borderBottom: '2.5px solid #0d1a1a',
                borderRight: '2.5px solid #0d1a1a',
                display: 'block',
              }}
            />
            <p
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: '10px',
                letterSpacing: '3px',
                color: 'rgba(13,26,26,0.3)',
                marginBottom: '12px',
              }}
            >
              {f.num}
            </p>
            <p
              style={{
                fontSize: '14px',
                fontWeight: 900,
                color: '#0d1a1a',
                marginBottom: '8px',
                letterSpacing: '0.5px',
                fontFamily: "'M PLUS Rounded 1c', sans-serif",
              }}
            >
              {f.title}
            </p>
            <p
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: 'rgba(13,26,26,0.5)',
                lineHeight: 1.7,
                fontFamily: "'M PLUS Rounded 1c', sans-serif",
              }}
            >
              {f.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Quote Section */}
      <div
        className={`relative z-10 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        style={{ background: '#0d1a1a', padding: '72px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}
      >
        <div
          style={{
            transform: `scale(${1 + Math.abs(mousePos.x) * 0.02})`,
            maxWidth: '720px',
            margin: '0 auto',
          }}
        >
          {/* animated teal line */}
          <div
            style={{
              height: '2px',
              background: '#7ecece',
              margin: '0 auto 32px',
              animation: 'onyxLineGrow 1.2s 0.3s forwards',
              width: 0,
            }}
          />
          <p
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 'clamp(24px, 4vw, 44px)',
              lineHeight: 1.15,
              letterSpacing: '1px',
              color: '#f0fafa',
              marginBottom: '16px',
            }}
          >
            "Throughout Heaven and Earth,{' '}
            <span style={{ color: '#7ecece' }}>I alone am the honored one."</span>
          </p>
          <p
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '3px',
              color: 'rgba(126,206,206,0.35)',
              fontFamily: "'M PLUS Rounded 1c', sans-serif",
            }}
          >
            — YOU KNOW WHO SAID IT.
          </p>
        </div>
      </div>

      {/* Final CTA */}
      <div
        className={`relative z-10 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        style={{
          background: '#7ecece',
          borderTop: '3px solid #0d1a1a',
          padding: '80px 40px',
          textAlign: 'center',
        }}
      >
        <h2
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 'clamp(40px, 8vw, 80px)',
            lineHeight: 0.9,
            marginBottom: '36px',
            color: '#0d1a1a',
            letterSpacing: '2px',
            textShadow: '3px 3px 0 rgba(13,26,26,0.15)',
          }}
        >
          <span style={{ display: 'block' }}>STEP IN AND YOU</span>
          <span
            style={{
              display: 'block',
              color: '#f0fafa',
              WebkitTextStroke: '1.5px #0d1a1a',
              textShadow: '2px 2px 0 #5ab8b8, 4px 4px 0 #0d1a1a',
            }}
          >
            MIGHT GET ISEKAI'D
          </span>
        </h2>

        <button
          onClick={handleEnter}
          disabled={isTransitioning}
          className="transition-all duration-100 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: '#0d1a1a',
            border: '3px solid #0d1a1a',
            borderRadius: '7px',
            padding: '15px 52px',
            fontFamily: "'M PLUS Rounded 1c', sans-serif",
            fontSize: '17px',
            fontWeight: 900,
            color: '#7ecece',
            letterSpacing: '2px',
            boxShadow: '4px 4px 0 rgba(13,26,26,0.35)',
            cursor: 'pointer',
          }}
          onMouseEnter={e => {
            const btn = e.currentTarget;
            btn.style.transform = 'translate(2px,2px)';
            btn.style.boxShadow = '2px 2px 0 rgba(13,26,26,0.35)';
          }}
          onMouseLeave={e => {
            const btn = e.currentTarget;
            btn.style.transform = '';
            btn.style.boxShadow = '4px 4px 0 rgba(13,26,26,0.35)';
          }}
        >
          JOIN ONYX
        </button>

        <p
          style={{
            marginTop: '14px',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '2px',
            color: 'rgba(13,26,26,0.45)',
            fontFamily: "'M PLUS Rounded 1c', sans-serif",
          }}
        >
          JOIN THE NETWORK · FREE FOREVER
        </p>
      </div>

      {/* Footer */}
      <div
        className={`relative z-10 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        style={{
          background: '#0d1a1a',
          borderTop: '3px solid rgba(13,26,26,0.8)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 32px',
        }}
      >
        <span
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '13px',
            letterSpacing: '3px',
            color: 'rgba(126,206,206,0.4)',
          }}
        >
          ONYX © 2026
        </span>
        <div style={{ display: 'flex', gap: '22px' }}>
          {['About', 'Terms'].map(link => (
            <button
              key={link}
              className="transition-colors duration-150"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '1px',
                color: 'rgba(126,206,206,0.35)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "'M PLUS Rounded 1c', sans-serif",
              }}
              onMouseEnter={e => { (e.target as HTMLButtonElement).style.color = '#7ecece'; }}
              onMouseLeave={e => { (e.target as HTMLButtonElement).style.color = 'rgba(126,206,206,0.35)'; }}
            >
              {link}
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=M+PLUS+Rounded+1c:wght@400;700;900&display=swap');

        @keyframes onyxPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.7); }
        }

        @keyframes onyxLineGrow {
          to { width: 60px; }
        }

        /* Domain Expansion Animations */
        @keyframes domain-expand {
          0%   { width: 0; height: 0; opacity: 1; }
          100% { width: 200vw; height: 200vw; opacity: 0; }
        }
        @keyframes domain-expand-delayed {
          0%   { width: 0; height: 0; opacity: 1; }
          100% { width: 180vw; height: 180vw; opacity: 0; }
        }
        @keyframes slice-in {
          0%   { transform: scaleY(0); }
          50%  { transform: scaleY(1); }
          100% { transform: scaleY(0); }
        }
        @keyframes power-flash {
          0%   { opacity: 0; }
          10%  { opacity: 0.8; }
          20%  { opacity: 0; }
          30%  { opacity: 0.6; }
          40%  { opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes text-fade-in {
          0%   { opacity: 0; transform: scale(0.5); }
          50%  { opacity: 0; transform: scale(0.5); }
          70%  { opacity: 1; transform: scale(1.2); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes text-fade-in-delayed {
          0%   { opacity: 0; transform: translateY(20px); }
          60%  { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes particle-float {
          0%   { transform: translate(0, 0) scale(0); opacity: 0; }
          50%  { opacity: 1; }
          100% { transform: translate(var(--tx, 100px), var(--ty, -100px)) scale(1); opacity: 0; }
        }

        .animate-domain-expand         { animation: domain-expand 1.5s cubic-bezier(0.4,0,0.2,1) forwards; }
        .animate-domain-expand-delayed { animation: domain-expand-delayed 1.5s cubic-bezier(0.4,0,0.2,1) 0.1s forwards; }
        .animate-slice-in              { animation: slice-in 1.2s cubic-bezier(0.4,0,0.2,1) forwards; }
        .animate-power-flash           { animation: power-flash 1s ease-out forwards; }
        .animate-domain-text           { animation: none; }
        .animate-text-fade-in          { animation: text-fade-in 1.5s cubic-bezier(0.4,0,0.2,1) forwards; }
        .animate-text-fade-in-delayed  { animation: text-fade-in-delayed 1.5s cubic-bezier(0.4,0,0.2,1) forwards; }
        .animate-particle-float        { animation: particle-float 1.5s ease-out forwards; }
      `}</style>
    </div>
  );
}